import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { Loader2, Search, Utensils, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useUpdateCookingSession } from "@/hooks/cooking-sessions/mutations";
import { userApi } from "@/lib/api/services/user.api";
import { useStations } from "@/hooks/useStations";
import { getApiErrorMessage } from "@/lib/api/api";

/** What an assignment replaced, so the caller can offer to put it back. */
export interface StationUndo {
  previousStationId: number | null;
}

interface AssignChefDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: number;
  previousChefId: number | null;
  /** The recipe's location: its stations are what the Station tab offers. Without it there is no Station tab. */
  locationId?: number;
  previousStationId?: number | null;
  /**
   * `previousChefId` is the chef to put back, or null when the chef was not part
   * of this assignment. `stationUndo` is only passed when a station was.
   */
  onAssigned?: (
    sessionId: number,
    previousChefId: number | null,
    assignedName: string,
    stationUndo?: StationUndo,
  ) => void;
}

type AssignTab = "chef" | "station";

/**
 * Assign a recipe to a chef, to a station, or both (YC-36). A kitchen with no
 * screen per chef assigns to the station and leaves the chef open.
 */
export function AssignChefDialog({
  open,
  onOpenChange,
  sessionId,
  previousChefId,
  locationId,
  previousStationId = null,
  onAssigned,
}: AssignChefDialogProps) {
  const { t } = useLingui();
  const [tab, setTab] = useState<AssignTab>("chef");
  const [search, setSearch] = useState("");
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);
  const [selectedChefId, setSelectedChefId] = useState<number | null>(null);
  const [selectedStationId, setSelectedStationId] = useState<number | null>(null);
  const updateSession = useUpdateCookingSession();

  const { data: stations, isLoading: stationsLoading } = useStations(
    { locationId },
    open && locationId !== undefined,
  );
  const showStationTab = locationId !== undefined;

  const { data: users, isLoading } = useQuery({
    queryKey: ["users", "assign-chef", search, "CHEF"],
    queryFn: () => userApi.paginate({ search, limit: 50, roleCode: "CHEF" }),
    enabled: open,
  });

  const allChefs = users?.data ?? [];
  const chefList = showAvailableOnly ? allChefs.filter((u) => u.isActive) : allChefs;

  function handleOpenChange(v: boolean) {
    onOpenChange(v);
    if (!v) {
      setSearch("");
      setSelectedChefId(null);
      setSelectedStationId(null);
      setTab("chef");
    }
  }

  /**
   * Wait for the write before claiming it happened (T-1102).
   *
   * This used to close the dialog and raise "X has been assigned" on the same
   * tick it called `mutate` — before the request had even left. Every failure
   * therefore looked exactly like a success, and the ticket read "Unassigned"
   * again on the next poll with nothing on screen to say why.
   */
  function handleConfirm() {
    if (!selectedChefId && !selectedStationId) return;
    const match = selectedChefId ? chefList.find((u) => Number(u.id) === selectedChefId) : undefined;
    const chefName = selectedChefId
      ? [match?.firstName, match?.lastName].filter(Boolean).join(" ") || match?.email || ""
      : "";
    const stationName = selectedStationId
      ? (stations?.find((s) => s.id === selectedStationId)?.name ?? "")
      : "";
    const assignedName = [chefName, stationName].filter(Boolean).join(" · ");
    updateSession.mutate(
      {
        id: sessionId,
        data: {
          ...(selectedChefId && { chefId: selectedChefId }),
          ...(selectedStationId && { stationId: selectedStationId }),
        },
      },
      {
        onSuccess: () => {
          handleOpenChange(false);
          if (selectedStationId) {
            onAssigned?.(sessionId, selectedChefId ? previousChefId : null, assignedName, { previousStationId });
          } else {
            onAssigned?.(sessionId, previousChefId, assignedName);
          }
        },
        // The dialog stays open, with the chef still selected, so the obvious
        // next move — try again — costs nothing.
        onError: (error) =>
          toast.error(
            getApiErrorMessage(
              error,
              selectedChefId ? t`Could not assign this chef` : t`Could not assign this station`,
            ),
          ),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-[56.25rem] p-0 gap-0 overflow-hidden" hideClose>
          {/* Header */}
          <div className="flex items-center justify-between p-5">
            <div className="flex flex-col gap-2">
              <DialogTitle className="text-2xl font-medium leading-6">
                {showStationTab ? t`Assign chef or station` : t`Assign chef`}
              </DialogTitle>
              <DialogDescription className="text-base">
                {showStationTab
                  ? t`Select a chef, a station, or both for this recipe.`
                  : t`Select a chef to assign to this recipe.`}
              </DialogDescription>
            </div>
            <DialogClose asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                <X className="h-4 w-4" />
                <span className="sr-only">{t`Close`}</span>
              </Button>
            </DialogClose>
          </div>

          {showStationTab && (
            <div className="flex items-center gap-2 px-5 pb-4" role="tablist">
              {(["chef", "station"] as AssignTab[]).map((name) => (
                <Button
                  key={name}
                  type="button"
                  role="tab"
                  aria-selected={tab === name}
                  variant="ghost"
                  onClick={() => setTab(name)}
                  className={cn(
                    "h-10 rounded-lg px-4 text-sm font-medium",
                    tab === name
                      ? "bg-(--color-surface-muted) text-(--color-text-primary)"
                      : "text-(--color-text-secondary)",
                  )}
                >
                  {name === "chef" ? t`Chef` : t`Station`}
                  {name === "chef" && selectedChefId !== null && <span className="ml-2 size-2 rounded-full bg-(--color-action)" />}
                  {name === "station" && selectedStationId !== null && <span className="ml-2 size-2 rounded-full bg-(--color-action)" />}
                </Button>
              ))}
            </div>
          )}

          {tab === "station" && showStationTab ? (
            <div className="px-5 max-h-100 overflow-y-auto flex flex-col gap-2 pb-5">
              {stationsLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-18 rounded-xl" />
                ))
              ) : !stations || stations.length === 0 ? (
                <div className="py-10 flex flex-col items-center gap-2 text-(--color-text-secondary)">
                  <p className="text-sm">{t`No stations set up for this location.`}</p>
                </div>
              ) : (
                stations.map((station) => {
                  const isSelected = selectedStationId === station.id;
                  const isCurrent = previousStationId === station.id;
                  return (
                    <Button
                      key={station.id}
                      type="button"
                      variant="ghost"
                      onClick={() => setSelectedStationId(station.id)}
                      className={cn(
                        "flex h-auto items-center justify-between w-full bg-(--color-surface-muted) rounded-xl p-4 text-left transition-colors hover:bg-(--color-surface-accent)",
                        isSelected && "ring-2 ring-primary",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-(--color-action)/10 text-(--color-action)">
                          <Utensils className="h-5 w-5" />
                        </div>
                        <span className="text-base font-medium text-(--color-text-primary)">{station.name}</span>
                        {isCurrent && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-(--color-surface-accent) text-(--color-text-secondary)">
                            {t`Current`}
                          </span>
                        )}
                      </div>
                      <div className={cn(
                        "h-6 w-6 rounded-[10px] border-[1.2px] border-(--color-border) bg-(--color-surface-content) shrink-0 transition-colors",
                        isSelected && "border-primary bg-(--color-action)",
                      )} />
                    </Button>
                  );
                })
              )}
            </div>
          ) : (
          <>
          {/* Toolbar */}
          <div className="flex items-center justify-between px-5 pb-5 gap-4">
            <div className="relative w-75">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-(--color-text-secondary)" />
              <Input
                placeholder={t`Search...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-11"
                autoFocus
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <Switch checked={showAvailableOnly} onCheckedChange={setShowAvailableOnly} />
              <span className="text-sm font-medium text-(--color-text-primary)">{t`Show available`}</span>
            </label>
          </div>

          {/* Chef list */}
          <div className="px-5 max-h-100 overflow-y-auto flex flex-col gap-2 pb-5">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-18 rounded-xl" />
              ))
            ) : chefList.length === 0 ? (
              <div className="py-10 flex flex-col items-center gap-2 text-(--color-text-secondary)">
                <p className="text-sm">{t`No chefs found.`}</p>
              </div>
            ) : (
              chefList.map((u) => {
                const name = [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email;
                const initials = name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
                const isAvailable = u.isActive;
                const isSelected = selectedChefId === Number(u.id);

                return (
                  <Button
                    key={u.id}
                    type="button"
                    variant="ghost"
                    onClick={() => setSelectedChefId(Number(u.id))}
                    className={cn(
                      "flex h-auto items-center justify-between w-full bg-(--color-surface-muted) rounded-xl p-4 text-left transition-colors hover:bg-(--color-surface-accent)",
                      isSelected && "ring-2 ring-primary",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <div className="relative h-10 w-10 shrink-0 rounded-full overflow-hidden bg-(--color-action)/10 flex items-center justify-center">
                        {u.profileImageUrl ? (
                          <img src={u.profileImageUrl} alt={name} className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-sm font-semibold text-(--color-action)">{initials}</span>
                        )}
                      </div>
                      <span className="text-base font-medium text-(--color-text-primary)">{name}</span>
                      <span className={cn(
                        "inline-flex items-center px-2 py-1 rounded-full text-sm",
                        isAvailable ? "bg-(--color-success) text-white" : "bg-(--color-surface-accent) text-(--color-text-secondary)",
                      )}>
                        {isAvailable ? t`Available` : t`Not available`}
                      </span>
                    </div>
                    <div className={cn(
                      "h-6 w-6 rounded-[10px] border-[1.2px] border-(--color-border) bg-(--color-surface-content) shrink-0 transition-colors",
                      isSelected && "border-primary bg-(--color-action)",
                    )} />
                  </Button>
                );
              })
            )}
          </div>
          </>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-5 py-5 border-t border-(--color-border) shadow-[0px_-8px_16px_rgba(16,24,40,0.04)]">
            <DialogClose asChild>
              <Button variant="outline" className="h-13 w-50 text-base font-medium">
                {t`Cancel`}
              </Button>
            </DialogClose>
            <Button
              disabled={(!selectedChefId && !selectedStationId) || updateSession.isPending}
              onClick={handleConfirm}
              className="h-13 w-50 text-base font-medium gap-2"
            >
              {updateSession.isPending && <Loader2 className="size-4 animate-spin" />}
              {t`Confirm`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
}
